import { useSelector } from 'react-redux';
import { LEGACY_TENANT_SLUGS } from '../../api/types';
import { tenantSelector } from '../../selector/authenticationSelector';
import { useIsMaster } from '../../common/useIsMaster';
import { useAccessibleTenants } from '../../common/useAccessibleTenants';
import { cn } from '../../utils/utils';
import TenantSwitcher from '../TenantSwitcher';
import { SideBarContent } from './SideBarContent';

interface SideBarProps {
    onHide: () => void;
    className?: string;
}

const SideBar = ({ onHide, className }: SideBarProps) => {
    const tenant = useSelector(tenantSelector);
    const isMaster = useIsMaster();
    // Tenants the caller can reach (legacy `tenant:<slug>` JWT permissions, plus
    // operational tenants for masters). A user who holds any of these can move
    // between tenants, so they must keep the switcher.
    const accessibleTenants = useAccessibleTenants();
    // `isSelfServiceTenant` is derived from the *selected* tenant, so it also
    // turns true when a master views an onboarded tenant. We only want to hide
    // the switcher for an actual self-service owner viewing their OWN single
    // tenant. A master, a user on a legacy tenant, or anyone who can reach
    // another tenant keeps it — otherwise, switching TO an onboarded tenant
    // (where the caller has no `app_user` row) would trap them with no way back.
    const isSelfServiceTenant = !!tenant && !LEGACY_TENANT_SLUGS.has(tenant);
    const canSwitchTenants = isMaster || !isSelfServiceTenant || accessibleTenants.length > 0;

    return (
        <div
            className={cn(
                'fixed bottom-0 left-0 top-16 overflow-hidden bg-text-contrast z-[1200] transition-all flex flex-1 flex-col',
                className,
            )}
        >
            <div className={'m-6 flex flex-1 flex-col gap-6'}>
                {canSwitchTenants && <TenantSwitcher />}
                <SideBarContent onClose={onHide} />
            </div>
        </div>
    );
};

export default SideBar;
